import { Component, OnInit } from '@angular/core';
import { MessageService } from 'primeng/api';
import { CommonModule } from '@angular/common';
import { ToastModule } from 'primeng/toast';
import { FormsModule } from '@angular/forms';
import { DropdownModule } from 'primeng/dropdown';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { TooltipModule } from 'primeng/tooltip';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { TabViewModule } from 'primeng/tabview';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { WorkerDialogComponent } from '../worker-dialog/worker-dialog.component';


import { catchError, map } from 'rxjs/operators';
import { Observable, of } from 'rxjs';


import { TranslateService } from '../../../services/translate.service';
import { WorkersInfoService } from './workers-info.service';
import { DirectionService } from '../../../services/direction.service';
import { ViewScopeService } from '../../../services/view-scope.service';
import { Pagination } from '../../../services/pagination.abstruct';

import { WorkersBasicRecord, WorkersEmailDataRecord, WorkersPhoneDataRecord, EditPhoneRow, NewPhoneRow, NewEmailRow, EditEmailRow} from '../../../models/workers-general-record.model';
import { SelectItem } from '../../../models/selected-item.model';




@Component({
	selector: 'app-workers-info',
	standalone: true,
	imports: [CommonModule, TableModule, FormsModule, DropdownModule, TooltipModule, TagModule, TabViewModule, ProgressSpinnerModule, ButtonModule, AutoCompleteModule, ToastModule, WorkerDialogComponent],
	templateUrl: './workers-info.component.html',
	styleUrls: ['./workers-info.component.scss'],
	providers: [MessageService]
})

export class WorkersInfoComponent extends Pagination implements OnInit {

	workers$!: Observable<WorkersBasicRecord[]>;
	allWorkers: WorkersBasicRecord[] = [];
	filteredWorkers: WorkersBasicRecord[] = [];
	globalFilter: string = '';
	statusFilter: string | null = null;

	public statusOptions!: any[];

	tableKey: string = 'workers-info-table';


	activeContactTabIndex: number = 0; // 0 - Phone, 1 - Email.

	newPhoneRow: { [workerId: number]: NewPhoneRow | null } = {};
	newEmailRow: { [workerId: number]: NewEmailRow | null}  = {};


	editPhoneRow: { [workerId: number]: { [phoneUserRecID: number]: EditPhoneRow | null } } = {};
	editEmailRow: { [workerId: number]: { [emailUserRecID: number]: EditEmailRow | null } } = {};

	phonePrefixes: SelectItem[] = [];
	phoneTypes: SelectItem[] = [];


	filteredPhonePrefixes: any[] = [];
	filteredPhoneTypes: any[] = [];

	// Per-worker contacts state: use only the existing models for data arrays
	contactsPhones: { [workerId: number]: WorkersPhoneDataRecord[] } = {};
	contactsEmails: { [workerId: number]: WorkersEmailDataRecord[] } = {};
	loadingPhones: { [workerId: number]: boolean } = {};
	loadingEmails: { [workerId: number]: boolean } = {};
	errorPhones: { [workerId: number]: string } = {};
	errorEmails: { [workerId: number]: string } = {};


	expandedRows: { [key: string]: boolean } = {};
	customersMap: { [key: number]: any[] } = {};
	loadingCustomers: { [key: number]: boolean } = {};

	// Assign-to-worker autocomplete state
	assignCandidates: WorkersBasicRecord[] = [];
	assignSuggestions: WorkersBasicRecord[] = [];
	showAssignInput: { [key: string]: boolean } = {};
	selectedAssignWorker: { [key: string]: WorkersBasicRecord | null } = {};
	loadingAssignCandidates: boolean = false;

	constructor(
		public translateServ: TranslateService,
		public workersInfoService: WorkersInfoService,
		public dirServ: DirectionService,
		private messageService: MessageService,
		private viewScopeService: ViewScopeService
	) {
		super();
		this.statusOptions = [
			{ label: this.translateServ.dictionary['MAINT_CS_ACT_SEL_VIEW_ALL'] || 'All', value: null },
			{ label: this.translateServ.dictionary['MAINT_CS_ACT_SEL_VIEW_ACT'] || 'Active', value: 'active' },
			{ label: this.translateServ.dictionary['MAINT_CS_ACT_SEL_VIEW_INACT'] || 'Inactive', value: 'inactive' }
		];
		// Load phone prefixes and types for autocomplete
		this.loadPhonePrefixes();
		this.loadPhoneTypes();
	}

	// Dialog state for create/edit worker
	showWorkerDialog: boolean = false;
	workerDialogMode: 'create' | 'edit' = 'create';
	workerDialogData: any = null;

	reloadData(): void 
	{
		this.workers$ = this.workersInfoService.loadAllMyWorkers();
		this.workers$.subscribe(data => {
			this.allWorkers = data;
			this.applyFilters();
		});
	}

	ngOnInit(): void 
	{
		this.reloadData();
	}

	loadPhonePrefixes() {
		this.workersInfoService.getPhonesPrefs().pipe(
			map((response: any) => {
				return (response || []).map((item: any) => ({
					label: item.Pref,
					value: item.Pref,
					code: item.PhonePrefRecID
				})) as SelectItem[];
			}),
			catchError(error => {
				console.error('Error fetching phone prefixes:', error);
				return of([]); // fallback: empty array
			})
		).subscribe((prefixes: SelectItem[]) => {
			this.phonePrefixes = prefixes;
			this.filteredPhonePrefixes = [...prefixes];
		});
	}

	loadPhoneTypes() {
		this.workersInfoService.getPhonesTypes().pipe(
			map((response: any) => {
				return (response || []).map((item: any) => ({
					label: item.PhoneType,
					value: item.PhoneType,
					code: item.PhoneTypeRecID
				})) as SelectItem[];
			}),
			catchError(error => {
				console.error('Error fetching phone types:', error);
				return of([]); // fallback: empty array
			})
		).subscribe((phoneTypes: SelectItem[]) => {
			this.phoneTypes = phoneTypes;
			this.filteredPhoneTypes = [...phoneTypes];
		});
	}

	filterPhonePrefixes(event: any) {
		const query = event.query ? event.query.toLowerCase() : '';
		this.filteredPhonePrefixes = this.phonePrefixes.filter(p => (p.label || '').toLowerCase().includes(query));
	}

	filterPhoneTypes(event: any) {
		const query = event.query ? event.query.toLowerCase() : '';
		this.filteredPhoneTypes = this.phoneTypes.filter(t => t.label.toLowerCase().includes(query));
	}

	onGlobalFilter(event: Event, dt: any) {
		const input = event.target as HTMLInputElement;
		this.globalFilter = input.value;
		this.applyFilters();
	}

	onStatusFilterChange(dt: any) {
		this.applyFilters();
	}

	clearFilters(dt: any) {
		this.globalFilter = '';
		this.statusFilter = null;
		this.applyFilters();
	}

	applyFilters() {
		let filtered = this.allWorkers;
		// Status filter
		filtered = this.statusFilter === 'active'
			? filtered.filter(w => w.IsActive == 1)
			: (this.statusFilter === 'inactive'
				? filtered.filter(w => w.IsActive == 0)
				: this.allWorkers);

		// Global filter (case-insensitive, checks all string fields)
		if (this.globalFilter && this.globalFilter.trim() !== '') {
			const filter = this.globalFilter.trim().toLowerCase();
			filtered = filtered.filter(w =>
				(Object.keys(w) as (keyof WorkersBasicRecord)[]).some(key => {
					const val = w[key];
					return val && typeof val === 'string' && val.toLowerCase().includes(filter);
				})
			);
		}
		this.filteredWorkers = filtered;
	}


	toggleRow(worker: WorkersBasicRecord) {
		const workerId = worker.UserRecID;
		if (this.expandedRows[workerId]) {
			delete this.expandedRows[workerId];
		} else {
			this.expandedRows[workerId] = true;
			if (!this.customersMap[workerId]) {
				this.loadingCustomers[workerId] = true;
				this.workersInfoService.getCustomersOfWorkers(workerId).subscribe(
					(customers: any[]) => {
						this.customersMap[workerId] = customers;
						this.loadingCustomers[workerId] = false;
					},
					() => {
						this.customersMap[workerId] = [];
						this.loadingCustomers[workerId] = false;
					}
				);
			}

			if (!this.contactsPhones[workerId]) this.loadPhonesForWorker(workerId); // Load phones if not already loaded.
			if (!this.contactsEmails[workerId]) this.loadEmailsForWorker(workerId); // Load emails if not already loaded.			
		}
	}


	private loadPhonesForWorker(workerId: number) {
		this.loadingPhones[workerId] = true;
		this.workersInfoService.loadWorkersPhones(workerId).subscribe(
			{
				next: (phones) => {
					this.contactsPhones[workerId] = phones;
					this.loadingPhones[workerId] = false;
				},
				error: () => {
					this.errorPhones[workerId] = 'Failed to load phones';
					this.loadingPhones[workerId] = false;
				}
			});
	}

	private loadEmailsForWorker(workerId: number) {
		this.loadingEmails[workerId] = true;
		this.workersInfoService.loadWorkersEmails(workerId).subscribe(
			{
				next: (emails) => {
					this.contactsEmails[workerId] = emails;
					this.loadingEmails[workerId] = false;
				},
				error: () => {
					this.errorEmails[workerId] = 'Failed to load emails';
					this.loadingEmails[workerId] = false;
				}
			});
	}

	isRowExpanded(worker: WorkersBasicRecord): boolean {
		return !!this.expandedRows[worker.UserRecID];
	}

	openCreateWorkerDialog() {
		this.workerDialogMode = 'create';
		this.workerDialogData = null;
		this.showWorkerDialog = true;
	}

	onWorkerSaved(event: any) {
		this.showInfo('Worker saved');
		this.showWorkerDialog = false;
		// Reload list to include new/updated worker
		this.reloadData();
	}

	onWorkerClosed() {
		this.showWorkerDialog = false;
		this.reloadData();
	}

	openEditWorkerDialog(worker: any)
	{
		this.workerDialogMode = 'edit';
		this.workerDialogData = worker || null;
		this.showWorkerDialog = true;
	}




	onCreateContact(worker: WorkersBasicRecord, tabIndex: number) 
	{
		// Only allow one new row at a time per worker
		if (tabIndex === 0) 
		{
			this.newPhoneRow[worker.UserRecID] = 
			{
				Pref: null,
				Number: '',
				Description: '',
				PhoneType: null,
				IsActive: 1,
				IsMain: false
			};
		} 
		else 
		{
			this.newEmailRow[worker.UserRecID] = 
			{
				Email: '',
				Description: '',
				IsActive: 1,
				IsMain: false
			};
		}
	}

	onCancelNewPhone(workerId: number) 
	{
		this.newPhoneRow[workerId] = null;
	}

	onCancelNewEmail(workerId: number) 
	{
		this.newEmailRow[workerId] = null;
	}

	onSaveNewPhone(workerId: number) {
		const newPhone = this.newPhoneRow[workerId];
		if (!newPhone) return;
		// Validate fields
		if (!newPhone.Pref || !newPhone.Number || newPhone.Number.length !== 7 || !/^[0-9]{7}$/.test(newPhone.Number)) {
			this.showWarn('Please enter a valid prefix and 7-digit phone number.');
			return;
		}
		if (!newPhone.PhoneType) {
			this.showWarn('Please select a phone type.');
			return;
		}

		const selectedPrefixObj = this.phonePrefixes.find(p => p.label === newPhone.Pref); // Find the selected prefix object to get PhonePrefRecID.
		const selectedPhoneTypeObj = this.phoneTypes.find(p => p.label === newPhone.PhoneType); // Find the selected phone type object to get PhoneTypeRecID.

		// Compose payload for backend
		const payload: any =
		{
			UserRecID: workerId,
			Phone: newPhone.Number,
			PhonePrefix: selectedPrefixObj ? selectedPrefixObj.label : null,
			PhonePrefRecID: selectedPrefixObj ? selectedPrefixObj.code : null,
			Descr: newPhone.Description,
			IsActive: newPhone.IsActive,
			PhoneTypeRecID: selectedPhoneTypeObj ? selectedPhoneTypeObj.code : null,
			IsMain: newPhone.IsMain ? 1 : 0,
			opUserRecID: this.viewScopeService.getCurrentOpertaingUserId()
		};

		console.log("Raw payload for phones:\n" + JSON.stringify(payload));


		this.workersInfoService.saveContact(0, this.activeContactTabIndex, JSON.stringify(payload)); // Call backend to save
		
		this.loadingPhones[workerId] = true;
		this.workersInfoService.loadWorkersPhones(workerId).subscribe({
			next: (phones) => {
				this.contactsPhones[workerId] = phones;
				this.loadingPhones[workerId] = false;
				this.newPhoneRow[workerId] = null;
				this.showInfo('Phone saved successfully.');

				this.loadPhonesForWorker(workerId);
			},
			error: () => {
				this.loadingPhones[workerId] = false;
				this.showWarn('Failed to save phone.');
			}
		});
	}

	onSaveEditPhone(workerId: number, phoneUserRecID: number, phoneRecID: number) {
		const editRow = this.editPhoneRow[workerId]?.[phoneUserRecID];
		if (!editRow) return;

		// Validate fields
		if (!editRow.Pref || !editRow.Number || editRow.Number.length !== 7 || !/^[0-9]{7}$/.test(editRow.Number)) {
			this.showWarn('Please enter a valid prefix and 7-digit phone number.');
			return;
		}
		if (!editRow.PhoneType) {
			this.showWarn('Please select a phone type.');
			return;
		}

		const selectedPrefixObj = this.findPhonePrefixRecIdByPrefix(editRow.Pref);
		const selectedPhoneTypeObj = this.findPhoneTypeRecIdByType(editRow.PhoneType);

		// Compose payload for backend
		const payload: any = {
			PhoneUserRecID: editRow.PhoneUserRecID,
			PhoneRecID: editRow.PhoneRecID,
			PhonePrefRecID: selectedPrefixObj ? selectedPrefixObj.code : null,
			PhoneTypeRecID: selectedPhoneTypeObj ? selectedPhoneTypeObj.code : null,
			UserRecID: editRow.UserRecID,
			Phone: editRow.Number,
			Descr: editRow.Descr,
			IsActive: editRow.IsActive,
			IsMain: editRow.IsMain,
			opUserRecID: this.viewScopeService.getCurrentOpertaingUserId()
		};

		console.log("Raw payload for edit phone:\n" + JSON.stringify(payload));
		this.workersInfoService.saveContact(1, this.activeContactTabIndex, JSON.stringify(payload));
		// Optionally, reload phones from backend after save
		this.loadingPhones[workerId] = true;
		this.workersInfoService.loadWorkersPhones(workerId).subscribe({
			next: (phones) => {
				this.contactsPhones[workerId] = phones;
				this.loadingPhones[workerId] = false;
				this.editPhoneRow[workerId][phoneUserRecID] = null;
				this.showInfo('Phone updated successfully.');
				this.loadPhonesForWorker(workerId);
			},
			error: () => {
				this.loadingPhones[workerId] = false;
				this.showWarn('Failed to update phone.');
			}
		});
	}

	// Helper to start editing a phone row
	startEditPhone(workerId: number, phone: WorkersPhoneDataRecord) {
		if (!this.editPhoneRow[workerId]) this.editPhoneRow[workerId] = {};
		// Parse prefix and number from PhoneNumber, remove any '-' between them
		let prefix = '';
		let number = '';
		if (phone.PhoneNumber && phone.PhoneNumber.length > 3) {
			// Remove any '-' after the prefix (e.g. '050-1234567' -> '050', '1234567')
			let raw = phone.PhoneNumber;
			if (raw[3] === '-') {
				prefix = raw.substring(0, 3);
				number = raw.substring(4);
			} else {
				prefix = raw.substring(0, 3);
				number = raw.substring(3);
			}
		}
		this.editPhoneRow[workerId][phone.PhoneUserRecID] = {
			PhoneUserRecID: phone.PhoneUserRecID,
			PhoneRecID: phone.PhoneRecID,
			PhonePrefRecID: phone.PhonePrefRecID,
			PhoneTypeRecID: phone.PhoneTypeRecID,
			UserRecID: workerId,
			Phone: phone.PhoneNumber,
			Descr: phone.Descr,
			IsActive: phone.IsActive,
			IsMain: phone.IsMain ? 1 : 0,
			opUserRecID: this.viewScopeService.getCurrentOpertaingUserId(),
			Number: number,
			Pref: prefix,
			PhoneType: phone.PhoneType
		};
	}

	cancelEditPhone(workerId: number, phoneUserRecID: number) {
		if (this.editPhoneRow[workerId]) {
			this.editPhoneRow[workerId][phoneUserRecID] = null;
		}
	}

	startEditEmail(workerId: number, email: WorkersEmailDataRecord) 
	{
		if (!this.editEmailRow[workerId]) this.editEmailRow[workerId] = {};
		this.editEmailRow[workerId][email.EmailRecID] = {
			EmailRecID: email.EmailRecID,
			EmailUserRecID: (email as any).EmailUserRecID ?? email.EmailRecID,
			UserRecID: workerId,
			Email: email.Email,
			Descr: email.Descr,
			IsActive: email.IsActive,
			IsMain: email.IsMain,
			opUserRecID: this.viewScopeService.getCurrentOpertaingUserId()
		};
	}

	cancelEditEmail(workerId: number, emailRecID: number) {
		if (this.editEmailRow[workerId]) {
			this.editEmailRow[workerId][emailRecID] = null;
		}
	}


	toggleContactIsMain(action: number, rowRecID: number, workerId: number) 
	{
		let isMain: 0 | 1 = 0;
		const data: WorkersEmailDataRecord[] | WorkersPhoneDataRecord[] = action == 0 ? this.contactsPhones[workerId] : this.contactsEmails[workerId];
		
		const row = data.find(r => 
		{
			return action === 0 && 'PhoneUserRecID' in r ? r.PhoneUserRecID === rowRecID: 
				   action === 1 && 'EmailRecID' in r ? r.EmailUserRecID === rowRecID : false;

		});

		if (row) isMain = (1 - row.IsMain) as 0 | 1; // Safe to use row here
		this.workersInfoService.setContactAsMain(action, rowRecID, isMain)?.subscribe(
		{
			next: () =>  this.refreshContacsOfWorker(action, workerId),
			error: () => this.refreshContacsOfWorker(action, workerId)
		});
	}


	onSaveNewEmail(workerId: number) {
		const newEmail = this.newEmailRow[workerId];
		if (!newEmail) return;
		// Validate fields
	
		if (!newEmail.Email) {
			this.showWarn('Please select an email.');
			return;
		}


		// Compose payload for backend
		const payload: any =
		{
			UserRecID: workerId,
			Email : newEmail.Email,
			Descr: newEmail.Description,
			IsActive: newEmail.IsActive,
			IsMain: newEmail.IsMain ? 1 : 0,
			opUserRecID: this.viewScopeService.getCurrentOpertaingUserId()
		};

		console.log("Raw payload for email:\n" + JSON.stringify(payload));


		this.workersInfoService.saveContact(0, this.activeContactTabIndex, JSON.stringify(payload)); // Call backend to save
		this.loadingEmails[workerId] = true;
		this.workersInfoService.loadWorkersEmails(workerId).subscribe({
			next: (emails) => {
				this.contactsEmails[workerId] = emails;
				this.loadingEmails[workerId] = false;
				this.newEmailRow[workerId] = null;
				this.showInfo('Email saved successfully.');

				this.loadEmailsForWorker(workerId);
			},
			error: () => {
				this.loadingEmails[workerId] = false;
				this.showWarn('Failed to save emails.');
			}
		});
	}


	onSaveEditEmail(workerId: number, emailUserRecID: number, emailRecID: number) {
		const editRow = this.editEmailRow[workerId]?.[emailUserRecID];
		if (!editRow) return;

		// Validate field
		if (!editRow.Email) {
			this.showWarn('Please stype email.');
			return;
		}


		// Compose payload for backend
		const payload: any = {
			EmailUserRecID: editRow.EmailUserRecID,
			EmailRecID : emailRecID,
			UserRecID: editRow.UserRecID,
			Email: editRow.Email,
			Descr: editRow.Descr,
			IsActive: editRow.IsActive,
			IsMain: editRow.IsMain,
			opUserRecID: this.viewScopeService.getCurrentOpertaingUserId()
		};

		console.log("Raw payload for edit email:\n" + JSON.stringify(payload));
		this.workersInfoService.saveContact(1,this.activeContactTabIndex, JSON.stringify(payload)); // 1 for update
		// Optionally, reload phones from backend after save
		this.loadingEmails[workerId] = true;
		this.workersInfoService.loadWorkersEmails(workerId).subscribe({
			next: (email) => {
				this.contactsEmails[workerId] = email;
				this.loadingPhones[workerId] = false;
				this.editEmailRow[workerId][emailRecID] = null;
				this.showInfo('Email updated successfully.');
				this.loadEmailsForWorker(workerId);
			},
			error: () => {
				this.loadingEmails[workerId] = false;
				this.showWarn('Failed to update email.');
			}
		});
	}


	showInfo(detail: string) {
		this.messageService.add({ severity: 'info', summary: 'Info', detail });
	}

	showWarn(detail: string) {
		this.messageService.add({ severity: 'warn', summary: 'Warning', detail });
	}

	toggleActiveNewPhone(workerId: number) 
	{
		if (this.newPhoneRow[workerId]) this.newPhoneRow[workerId]!.IsActive = this.newPhoneRow[workerId]!.IsActive ? 0 : 1;
	}

	toggleActiveNewEmail(workerId: number)
	{
		if (this.newEmailRow[workerId]) this.newEmailRow[workerId]!.IsActive = this.newEmailRow[workerId]!.IsActive ? 0 : 1;
	}


	toggleConstactActivity(action : number, workerId: number, rowRecID: number)
	{
		const data: WorkersEmailDataRecord[] | WorkersPhoneDataRecord[] = action == 0 ? this.contactsPhones[workerId] : this.contactsEmails[workerId];
		const row = data.find(r => 
		{
			return action === 0 && 'PhoneUserRecID' in r ? r.PhoneUserRecID === rowRecID: action === 1 && 'EmailRecID' in r ? r.EmailRecID === rowRecID : false;
		});
		
		if(row) row.IsActive = row?.IsActive != null ? (1 - Number(row.IsActive)) as 0 | 1 : 0;
		this.workersInfoService.setContactActivity(action, rowRecID, row!.IsActive as 0 | 1);
	}

	toggleMainNewPhone(workerId: number) {
		if (this.newPhoneRow[workerId]) this.newPhoneRow[workerId]!.IsMain = !this.newPhoneRow[workerId]!.IsMain;
	}

	toggleMainNewEmail(workerId: number) {
		if (this.newEmailRow[workerId]) this.newEmailRow[workerId]!.IsMain = !this.newEmailRow[workerId]!.IsMain;
	}

	deleteContact(action : number, communicationalItemId: number, workerId: number)
	{
		this.workersInfoService.deleteContact(action, communicationalItemId)?.subscribe(
		{
			next: () =>  this.refreshContacsOfWorker(action, workerId),
			error: () => console.error("Could not delete item for worker: ", workerId)
		});
	}

	private findPhoneTypeRecIdByType(inputType: string) {
		return this.phonePrefixes.find(p => p.label === inputType);
	}

	private findPhonePrefixRecIdByPrefix(inputPrefix: string) {
		return this.phoneTypes.find(p => p.label === inputPrefix);
	}

	private refreshContacsOfWorker(action: number, workerId: number)
	{
		action === 0 ? this.loadPhonesForWorker(workerId) : this.loadEmailsForWorker(workerId)
	}

	// Refresh assigned customers for a worker
	private refreshCustomers(workerId: number) {
		this.loadingCustomers[workerId] = true;
		this.workersInfoService.getCustomersOfWorkers(workerId).subscribe(
			(customers: any[]) => {
				this.customersMap[workerId] = customers;
				this.loadingCustomers[workerId] = false;
			},
			() => {
				this.customersMap[workerId] = [];
				this.loadingCustomers[workerId] = false;
			}
		);
	}

	// Open the assign input for a specific customer row; load candidates first time
	openAssignInput(sourceWorkerId: number, customer: any) {
		const key = sourceWorkerId + '_' + (customer.userCustomerRecID ?? customer.UserCustomerRecID ?? customer.id);
		this.showAssignInput[key] = !this.showAssignInput[key];
		if (this.showAssignInput[key] && this.assignCandidates.length === 0) {
			this.loadingAssignCandidates = true;
			this.workersInfoService.loadAllMyWorkers().pipe(
				map(list => list.filter(w => w.RoleRecID === 2))
			).subscribe(
				(list) => {
					this.assignCandidates = list;
					this.loadingAssignCandidates = false;
				},
				() => {
					this.assignCandidates = [];
					this.loadingAssignCandidates = false;
				}
			);
		}
	}

	filterAssign(event: any) {
		const query = event.query ? event.query.toLowerCase() : '';
		this.assignSuggestions = this.assignCandidates
			.map(w => ({
				...w,
				label: `${w.UserRecID} ${w.FirstName || ''} ${w.LastName || ''}`.trim()
			}))
			.filter((w: any) => {
				const name = (w.label + ' ' + (w.UserName || '')).toLowerCase();
				return name.includes(query);
			});
	}

	onAssignSelect(event: any, sourceWorkerId: number, customer: any) {
		// Store the selected worker in the temp selection; do NOT perform assign until confirmed
		const selected: WorkersBasicRecord | undefined = event?.value ?? event;
		if (!selected) {
			this.showWarn('No worker selected.');
			return;
		}
		const key = sourceWorkerId + '_' + (customer.userCustomerRecID ?? customer.UserCustomerRecID ?? customer.id ?? customer.relId);
		this.selectedAssignWorker[key] = selected;
	}

	// Confirm assign button handler: perform assignment using selectedAssignWorker
	onConfirmAssign(sourceWorkerId: number, customer: any) 
	{
		const key = sourceWorkerId + '_' + (customer.userCustomerRecID ?? customer.UserCustomerRecID ?? customer.id ?? customer.relId);
		const selected: WorkersBasicRecord | null = this.selectedAssignWorker[key] ?? null;
		if (!selected) {
			this.showWarn('Please select a worker to assign to.');
			return;
		}
		const userCustomerRecID = customer.relId;
		const targetWorkerId = selected.UserRecID;
		this.workersInfoService.assignCustomerToWorker(userCustomerRecID, targetWorkerId).subscribe({
			next: (response: any) => {
				this.showInfo('Customer reassigned.');
				this.refreshCustomers(sourceWorkerId);
				this.refreshCustomers(targetWorkerId);
				this.showAssignInput[key] = false;
				this.selectedAssignWorker[key] = null;
			},
			error: (err: any) => {
				console.error('Error reassigning customer:', err);
				this.showWarn('Failed to reassign customer.');
			}
		});
	}

	cancelAssignInput(sourceWorkerId: number, customer: any) {
		const key = sourceWorkerId + '_' + (customer.userCustomerRecID ?? customer.UserCustomerRecID ?? customer.id);
		this.showAssignInput[key] = false;
		this.selectedAssignWorker[key] = null;
	}

}